import {
  Command,
  Plugin,
  ButtonView,
  Writer,
  DowncastWriter,
  ViewElement,
  Widget,
  toWidget,
  viewToModelPositionOutsideModelElement,
  ViewDocumentFragment,
  HtmlDataProcessor,
} from "ckeditor5";
import { CustomTags } from "../../../../../shared/entities/custom-tags";

export class Placeholder extends Plugin {
  static get requires() {
    return [PlaceholderEditing, PlaceholderUI];
  }
}

export const CKEditorTags: Record<string, string> = {
  "publicationTemplate.create.dynamicTags.campaignUrl": CustomTags.CampaignUrl,
  "publicationTemplate.create.dynamicTags.publisherUrl":
    CustomTags.PublisherUrl,
};

class PlaceholderCommand extends Command {
  execute({ value }: { value: string }): void {
    const editor = this.editor;
    const selection = editor.model.document.selection;

    editor.model.change((writer: Writer) => {
      const placeholder = writer.createElement("placeholder", {
        ...Object.fromEntries(selection.getAttributes()),
        name: value,
      });

      editor.model.insertObject(placeholder, null, null, {
        setSelection: "on",
      });

      const positionAfter = writer.createPositionAfter(placeholder);
      writer.setSelection(positionAfter);
    });
  }

  refresh(): void {
    const model = this.editor.model;
    const selection = model.document.selection;

    this.isEnabled = model.schema.checkChild(
      selection.focus!.parent as any,
      "placeholder",
    );
  }
}

class PlaceholderUI extends Plugin {
  init(): void {
    const editor = this.editor;
    const placeholderNames = editor.config.get(
      "placeholderConfig.types",
    ) as string[];

    placeholderNames.forEach((name: string) => {
      this._createPlaceholderButton(name);
    });
  }

  private _createPlaceholderButton(name: string): void {
    const editor = this.editor;
    const command = editor.commands.get("placeholder") as PlaceholderCommand;
    const buttonView = new ButtonView();

    buttonView.set({
      label: name.charAt(0).toUpperCase() + name.slice(1),
      withText: true,
      tooltip: true,
    });

    buttonView.render();
    buttonView.bind("isEnabled").to(command);

    this.listenTo(buttonView, "execute", () => {
      editor.execute("placeholder", { value: name });
      editor.editing.view.focus();
    });

    const footerElement = document.getElementById("editor-footer");
    if (footerElement && buttonView.element) {
      footerElement.appendChild(buttonView.element);
    }
  }
}

class PlaceholderEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init(): void {
    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add(
      "placeholder",
      new PlaceholderCommand(this.editor),
    );

    this.editor.editing.mapper.on(
      "viewToModelPosition",
      viewToModelPositionOutsideModelElement(
        this.editor.model,
        (viewElement: ViewElement) => viewElement.hasClass("placeholder"),
      ),
    );

    this.editor.config.define("placeholderConfig", {
      types: Object.keys(CKEditorTags),
    });
  }

  private _defineSchema(): void {
    const schema = this.editor.model.schema;

    schema.register("placeholder", {
      inheritAllFrom: "$inlineObject",
      allowAttributes: ["name"],
    });
  }

  private _defineConverters(): void {
    const conversion = this.editor.conversion;
    const processor = this.editor.data.processor;

    conversion.for("upcast").elementToElement({
      view: {
        name: "span",
        classes: ["placeholder"],
      },
      model: (
        viewElement: ViewElement,
        { writer: modelWriter }: { writer: Writer },
      ) => {
        const childNode = viewElement.getChild(0);

        let name = "";
        if (childNode && childNode.is("$text")) {
          name = (childNode as any).data;
        }

        return modelWriter.createElement("placeholder", { name: name });
      },
    });

    conversion.for("editingDowncast").elementToElement({
      model: "placeholder",
      view: (
        modelItem: Element,
        { writer: viewWriter }: { writer: DowncastWriter },
      ) => {
        const widgetElement = createPlaceholderView(modelItem, viewWriter);
        return toWidget(widgetElement, viewWriter);
      },
    });

    conversion.for("dataDowncast").elementToElement({
      model: "placeholder",
      view: (
        modelItem: Element,
        { writer: viewWriter }: { writer: DowncastWriter },
      ) => createPlaceholderView(modelItem, viewWriter),
    });

    const originalToView = processor.toView.bind(processor);
    processor.toView = (data: string): ViewDocumentFragment => {
      const viewFragment = originalToView(data);
      const htmlProcessor = new HtmlDataProcessor(
        this.editor.editing.view.document,
      );
      const html = htmlProcessor.toData(viewFragment);
      const processedHtml = parseTagsIntoPlaceholders(html);
      return originalToView(processedHtml);
    };

    const originalToData = processor.toData.bind(processor);
    processor.toData = (viewFragment: ViewDocumentFragment): string => {
      const htmlProcessor = new HtmlDataProcessor(
        this.editor.editing.view.document,
      );
      const html = htmlProcessor.toData(viewFragment);
      const processedHtml = parsePlaceholdersIntoTags(html);
      const processedFragment = htmlProcessor.toView(processedHtml);
      return originalToData(processedFragment);
    };

    function createPlaceholderView(
      modelItem: Element,
      viewWriter: DowncastWriter,
    ) {
      const name = modelItem.getAttribute("name");

      const placeholderView = viewWriter.createContainerElement("span", {
        class: "placeholder ck-widget",
      });

      const innerText = viewWriter.createText(`${name}`);
      viewWriter.insert(
        viewWriter.createPositionAt(placeholderView, 0),
        innerText,
      );

      return placeholderView;
    }
  }
}

export function parseTagsIntoPlaceholders(content: string): string {
  const pattern = new RegExp(`(${Object.values(CKEditorTags).join("|")})`, "g");

  return content.replace(pattern, (match, tagValue) => {
    const tagKey = Object.keys(CKEditorTags).find(
      (key) => CKEditorTags[key] === tagValue,
    );

    return tagKey
      ? `<span class="placeholder ck-widget">${tagKey}</span>`
      : match;
  });
}

export function parsePlaceholdersIntoTags(content: string): string {
  return content.replace(
    /<span class="placeholder ck-widget"[^>]*>(.*?)<\/span>/g,
    (match, p1) => CKEditorTags[p1] ?? match,
  );
}
